import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Download, Calendar } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/components/Pagination";
import usePaginationLinks from "@/hooks/usePaginationLinks";
import CutoffAnalyticsTable from "@/components/CutoffAnalyticsTable";

const PAGE_SIZE = 30;

export default function ListPage({ kind = "admit-cards", title = "Admit Cards", subtitle = "Latest exam admit cards" }) {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState(params.get("search") || "");
  const [loading, setLoading] = useState(true);
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const search = params.get("search") || "";

  const fetchData = async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    sp.append("limit", String(PAGE_SIZE));
    sp.append("skip", String((page - 1) * PAGE_SIZE));
    if (search) sp.append("search", search);
    const { data } = await api.get(`/${kind}?${sp}`);
    setItems(data.items); setTotal(data.total); setLoading(false);
  };

  useEffect(() => { fetchData(); }, [kind, page, search]); // eslint-disable-line

  // Per-page SEO: title, meta description, canonical
  useEffect(() => {
    const SITE = "https://hireds.in";
    const isAdmit = kind === "admit-cards";
    const oldTitle = document.title;
    const oldDescEl = document.querySelector('meta[name="description"]');
    const oldDesc = oldDescEl?.content;
    const oldCanonEl = document.querySelector('link[rel="canonical"]');
    const oldCanon = oldCanonEl?.href;
    document.title = isAdmit
      ? "Admit Card 2026 — Latest Indian Government Exam Hall Tickets | hireds.in"
      : "Result 2026 — Latest Indian Government Exam Results | hireds.in";
    if (oldDescEl) {
      oldDescEl.content = isAdmit
        ? "Download all latest Indian govt exam admit cards / hall tickets on hireds.in — SSC, IBPS, RRB, UPSC, State PSC. Updated daily, official source links only."
        : "Check all latest Indian govt exam results on hireds.in — SSC, IBPS, RRB, UPSC, State PSC. Updated daily with official scorecard & cutoff links.";
    }
    if (oldCanonEl) oldCanonEl.href = `${SITE}/${kind}`;
    return () => {
      document.title = oldTitle;
      if (oldDescEl && oldDesc) oldDescEl.content = oldDesc;
      if (oldCanonEl && oldCanon) oldCanonEl.href = oldCanon;
    };
  }, [kind]);

  usePaginationLinks(`/${kind}`, Object.fromEntries(params), page, totalPages);

  const submitSearch = () => {
    const np = new URLSearchParams(params);
    if (q) np.set("search", q); else np.delete("search");
    np.delete("page");
    setParams(np);
  };

  const goToPage = (p) => {
    const np = new URLSearchParams(params);
    if (p > 1) np.set("page", String(p)); else np.delete("page");
    setParams(np);
  };

  const isAdmit = kind === "admit-cards";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid={`listpage-${kind}`}>
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{title}</h1>
        <p className="text-slate-600 text-sm mt-1">{subtitle} · {total} total{totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}</p>
      </div>
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
        <Input data-testid={`${kind}-search`} value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter') submitSearch(); }} placeholder="Search…" className="pl-9 max-w-md"/>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-bold text-slate-900">Title</TableHead>
              <TableHead className="font-bold text-slate-900 hidden md:table-cell">Organization</TableHead>
              <TableHead className="font-bold text-slate-900 hidden md:table-cell">{isAdmit?"Exam Date":"Result Date"}</TableHead>
              <TableHead className="font-bold text-slate-900 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-12">Loading…</TableCell></TableRow>}
            {!loading && items.length===0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-12">No records found.</TableCell></TableRow>}
            {items.map((it, i)=>(
              <TableRow key={it.id} data-testid={`${kind}-row-${i}`} className="hover:bg-orange-50/40">
                <TableCell className="font-medium text-slate-900">
                  <Link to={`/${kind}/${it.slug}`} className="hover:text-orange-600">{it.title}</Link>
                </TableCell>
                <TableCell className="hidden md:table-cell text-slate-600 text-sm">{it.organization}</TableCell>
                <TableCell className="hidden md:table-cell text-slate-600 text-sm"><span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>{fmtDate(isAdmit?it.exam_date:it.result_date)}</span></TableCell>
                <TableCell className="text-right">
                  <Link to={`/${kind}/${it.slug}`} className="text-orange-600 font-semibold text-sm hover:underline inline-flex items-center gap-1"><Download className="w-3.5 h-3.5"/>{isAdmit?"Download":"Check"}</Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} totalPages={totalPages} onPageChange={goToPage} />

      {/* Cut-off analytics archive — shown on Result page only */}
      {!isAdmit && <CutoffAnalyticsTable />}
    </div>
  );
}
